// Menu sounds: one delegated listener for the whole app instead of a call in every component.
// Buttons, tabs, links and checkboxes click; primary buttons confirm; `data-sound` overrides the
// choice ("none" silences an element, any UI sound name picks that one). Hovering a button or tab
// ticks, with a short gap so sweeping the mouse over a list does not rattle.

import type { AudioEngine } from "../audio";

const CLICKABLE = "button, [role='button'], [role='tab'], a[href], input[type='checkbox'], input[type='radio'], summary";
const HOVERABLE = "button, [role='button'], [role='tab'], a[href]";
/** Shortest time between two hover ticks (ms). */
const HOVER_GAP_MS = 60;

/** The UI sound for a click on `el`, or null when it makes none. */
export function clickSound(el: Element): string | null {
  const target = el.closest(CLICKABLE);
  if (!target || target.matches(":disabled, [aria-disabled='true']")) return null;
  const override = target.closest("[data-sound]")?.getAttribute("data-sound");
  if (override !== null && override !== undefined) return override === "none" ? null : override;
  return target.classList.contains("btn--primary") ? "confirm" : "click";
}

export function installUiSounds(audio: Pick<AudioEngine, "playUi">): void {
  let lastHover = 0;
  document.addEventListener(
    "click",
    (e) => {
      if (!(e.target instanceof Element)) return;
      const sound = clickSound(e.target);
      if (sound === null) return;
      audio.playUi(sound);
    },
    { capture: true },
  );
  document.addEventListener(
    "pointerover",
    (e) => {
      if (e.pointerType !== "mouse" || document.pointerLockElement || !(e.target instanceof Element)) return;
      const target = e.target.closest(HOVERABLE);
      if (!target || target.contains(e.relatedTarget as Node | null) || target.matches(":disabled, [aria-disabled='true']")) return;
      if (target.closest("[data-sound='none']")) return;
      const now = performance.now();
      if (now - lastHover < HOVER_GAP_MS) return;
      lastHover = now;
      audio.playUi("hover");
    },
    { capture: true, passive: true },
  );
}
